export default {
  namespaced: true,
  
  state: {
    videoId: 'RcGyVTAoXEU',
    playerInstance: null,
    playerKey: 'initial-player',
    startTime: 9,
    currentTime: 0,
    playerState: 2, // 1=playing, 2=paused
    playbackRate: 1,
    timeUpdateInterval: null,
    playerStateInterval: null
  },
  
  mutations: {
    SET_PLAYER_INSTANCE(state, player) {
      state.playerInstance = player;
    },
    
    SET_VIDEO_ID(state, videoId) {
      state.videoId = videoId;
    },
    
    SET_PLAYER_KEY(state, key) {
      state.playerKey = key;
    },
    
    SET_START_TIME(state, time) {
      state.startTime = time;
    },
    
    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },
    
    SET_PLAYER_STATE(state, playerState) {
      state.playerState = playerState;
    },

    SET_PLAYBACK_RATE(state, rate) {
      state.playbackRate = rate;
    },
    
    SET_TIME_UPDATE_INTERVAL(state, interval) {
      if (state.timeUpdateInterval) {
        clearInterval(state.timeUpdateInterval);
      }
      state.timeUpdateInterval = interval;
    },
    
    SET_PLAYER_STATE_INTERVAL(state, interval) {
      if (state.playerStateInterval) {
        clearInterval(state.playerStateInterval);
      }
      state.playerStateInterval = interval;
    }
  },
  
  actions: {
    initializePlayer({ commit, state, dispatch }, player) {
      console.log("Initializing player in store", player);
      
      if (!player) {
        console.error("Invalid player instance provided");
        return;
      }
      
      commit('SET_PLAYER_INSTANCE', player);
      
      if (typeof player.getCurrentTime !== 'function' || typeof player.getPlayerState !== 'function') {
        console.warn("Player instance may not have all required methods. Using player.player if available.");
        
        if (player.player && typeof player.player.getCurrentTime === 'function') {
          // This is likely the wrapper component - we'll handle it specially in our intervals
        } else {
          console.error("Player instance does not have required methods and no fallback is available");
        }
      }
      
      dispatch('startPlayerStateInterval');

      dispatch('startTimeUpdateInterval');

      if (state.playbackRate !== 1) {
        dispatch('applyPlaybackRate');
      }
      
      dispatch('safeGetCurrentTime').then(time => {
        if (!isNaN(time)) {
          commit('SET_CURRENT_TIME', Math.floor(time));
        }
      }).catch(err => {
        console.error('Error getting initial player time:', err);
      });
    },
    
    safeGetCurrentTime({ state }) {
      return new Promise((resolve, reject) => {
        if (!state.playerInstance) {
          reject(new Error('No player instance available'));
          return;
        }
        
        if (typeof state.playerInstance.getCurrentTime === 'function') {
          state.playerInstance.getCurrentTime().then(resolve).catch(reject);
        } 
        else if (state.playerInstance.player && typeof state.playerInstance.player.getCurrentTime === 'function') {
          state.playerInstance.player.getCurrentTime().then(resolve).catch(reject);
        } 
        else if (typeof state.playerInstance.currentTime !== 'undefined') {
          resolve(state.playerInstance.currentTime);
        } 
        else {
          reject(new Error('Cannot access currentTime from player instance'));
        }
      });
    },

    // Caption-derived sentence ends can overshoot the video length, and YouTube's
    // seekTo() past duration leaves playback in an unpredictable position.
    safeGetDuration({ state }) {
      return new Promise((resolve, reject) => {
        if (!state.playerInstance) {
          reject(new Error('No player instance available'));
          return;
        }

        if (typeof state.playerInstance.getDuration === 'function') {
          state.playerInstance.getDuration().then(resolve).catch(reject);
        }
        else if (state.playerInstance.player && typeof state.playerInstance.player.getDuration === 'function') {
          state.playerInstance.player.getDuration().then(resolve).catch(reject);
        }
        else {
          reject(new Error('Cannot access duration from player instance'));
        }
      });
    },

    safeGetPlayerState({ state }) {
      return new Promise((resolve, reject) => {
        if (!state.playerInstance) {
          reject(new Error('No player instance available'));
          return;
        }
        
        if (typeof state.playerInstance.getPlayerState === 'function') {
          state.playerInstance.getPlayerState().then(resolve).catch(reject);
        } 
        else if (state.playerInstance.player && typeof state.playerInstance.player.getPlayerState === 'function') {
          state.playerInstance.player.getPlayerState().then(resolve).catch(reject);
        } 
        else if (typeof state.playerInstance.playerState !== 'undefined') {
          resolve(state.playerInstance.playerState);
        } 
        else {
          reject(new Error('Cannot access playerState from player instance'));
        }
      });
    },
    
    startTimeUpdateInterval({ commit, state, dispatch }) {
      if (state.timeUpdateInterval) {
        clearInterval(state.timeUpdateInterval);
      }
      
      const interval = setInterval(() => {
        if (state.playerInstance) {
          dispatch('safeGetCurrentTime').then(currentTime => {
            if (!isNaN(currentTime)) {
              commit('SET_CURRENT_TIME', Math.floor(currentTime));
            }
          }).catch(err => {
            console.error('Error getting current time:', err);
          });
        }
      }, 1000);
      
      commit('SET_TIME_UPDATE_INTERVAL', interval);
    },
    
    startPlayerStateInterval({ commit, state, dispatch }) {
      if (state.playerStateInterval) {
        clearInterval(state.playerStateInterval);
      }
      
      const interval = setInterval(() => {
        if (state.playerInstance) {
          dispatch('safeGetPlayerState').then(playerState => {
            commit('SET_PLAYER_STATE', playerState);
          }).catch(err => {
            // Only log once in a while to avoid console spam
            if (Math.random() < 0.1) {
              console.error('Error getting player state:', err);
            }
          });
        }
      }, 500);
      
      commit('SET_PLAYER_STATE_INTERVAL', interval);
    },
    
    togglePlayback({ state, dispatch }) {
      if (!state.playerInstance) return;
      
      dispatch('safeGetPlayerState').then(playerState => {
        if (playerState === 1) { // Playing
          dispatch('pauseVideo');
        } else {
          dispatch('resumeVideo');
        }
      }).catch(() => {
        // If we can't get state, try to toggle based on our last known state
        if (state.playerState === 1) {
          dispatch('pauseVideo');
        } else {
          dispatch('resumeVideo');
        }
      });
    },
    
    pauseVideo({ state }) {
      if (!state.playerInstance) return;
      
      try {
        if (typeof state.playerInstance.pause === 'function') {
          state.playerInstance.pause();
        }
        else if (state.playerInstance.player && typeof state.playerInstance.player.pause === 'function') {
          state.playerInstance.player.pause();
        }
        else if (typeof state.playerInstance.pauseVideo === 'function') {
          state.playerInstance.pauseVideo();
        }
        else {
          console.error('No method to pause video found on player instance');
        }
      } catch (e) {
        console.error('Error pausing video:', e);
      }
    },
    
    resumeVideo({ state }) {
      if (!state.playerInstance) return;
      
      try {
        if (typeof state.playerInstance.resume === 'function') {
          state.playerInstance.resume();
        }
        else if (state.playerInstance.player && typeof state.playerInstance.player.resume === 'function') {
          state.playerInstance.player.resume();
        }
        else if (typeof state.playerInstance.playVideo === 'function') {
          state.playerInstance.playVideo();
        }
        else {
          console.error('No method to resume video found on player instance');
        }
      } catch (e) {
        console.error('Error resuming video:', e);
      }
    },
    
    seekForward({ state, commit, dispatch }, seconds = 3) {
      if (!state.playerInstance) return;
      
      dispatch('safeGetCurrentTime').then(time => {
        const newTime = time + seconds;
        dispatch('seekTo', newTime);
        commit('SET_START_TIME', Math.floor(newTime));
      }).catch(e => {
        console.error('Error seeking forward:', e);
      });
    },
    
    seekBackward({ state, commit, dispatch }, seconds = 3) {
      if (!state.playerInstance) return;
      
      dispatch('safeGetCurrentTime').then(time => {
        const newTime = Math.max(0, time - seconds);
        dispatch('seekTo', newTime);
        commit('SET_START_TIME', Math.floor(newTime));
      }).catch(e => {
        console.error('Error seeking backward:', e);
      });
    },
    
    seekTo({ state }, time) {
      if (!state.playerInstance) return;
      
      try {
        if (typeof state.playerInstance.seekTo === 'function') {
          state.playerInstance.seekTo(time);
        }
        else if (state.playerInstance.player && typeof state.playerInstance.player.seekTo === 'function') {
          state.playerInstance.player.seekTo(time);
        }
        else {
          console.error('No method to seek found on player instance');
        }
      } catch (e) {
        console.error('Error seeking to time:', e);
      }
    },
    
    resetToStart({ state, commit, dispatch }) {
      if (!state.playerInstance) return;
      
      dispatch('seekTo', state.startTime);
      commit('SET_CURRENT_TIME', state.startTime);
      
      dispatch('safeGetPlayerState').then(playerState => {
        if (playerState === 1) { // If playing
          dispatch('pauseVideo');
        }
      }).catch(() => {
        // If we can't check state, just pause to be safe
        dispatch('pauseVideo');
      });
    },
    
    setVideoId({ commit }, videoId) {
      commit('SET_VIDEO_ID', videoId);
      commit('SET_PLAYER_KEY', Date.now() + '-player'); // Force player recreation
    },
    
    setStartTime({ commit, state, dispatch }, time) {
      commit('SET_START_TIME', time);
      commit('SET_CURRENT_TIME', time);

      // Apply to the live player; at boot (loadSavedSettings) there is no
      // instance yet and the player mounts with the right start time itself
      if (state.playerInstance) {
        dispatch('seekTo', time);
        dispatch('pauseVideo');
      }
    },

    setPlaybackRate({ commit, dispatch }, rate) {
      commit('SET_PLAYBACK_RATE', rate);
      localStorage.setItem('ezshadowPlaybackRate', String(rate));
      dispatch('applyPlaybackRate');
    },

    cyclePlaybackRate({ state, dispatch }) {
      const rates = [1, 0.75, 0.5];
      const nextIndex = (rates.indexOf(state.playbackRate) + 1) % rates.length;
      dispatch('setPlaybackRate', rates[nextIndex]);
    },

    applyPlaybackRate({ state }) {
      if (!state.playerInstance) return;

      try {
        if (typeof state.playerInstance.setPlaybackRate === 'function') {
          state.playerInstance.setPlaybackRate(state.playbackRate);
        }
        else if (state.playerInstance.player && typeof state.playerInstance.player.setPlaybackRate === 'function') {
          state.playerInstance.player.setPlaybackRate(state.playbackRate);
        }
        else {
          console.error('No method to set playback rate found on player instance');
        }
      } catch (e) {
        console.error('Error setting playback rate:', e);
      }
    },
    
    cleanupPlayer({ commit, state }) {
      if (state.timeUpdateInterval) {
        clearInterval(state.timeUpdateInterval);
        commit('SET_TIME_UPDATE_INTERVAL', null);
      }
      
      if (state.playerStateInterval) {
        clearInterval(state.playerStateInterval);
        commit('SET_PLAYER_STATE_INTERVAL', null);
      }

      // Clear player reference and reset state so a remounted screen
      // never acts on a stale "playing" status
      commit('SET_PLAYER_INSTANCE', null);
      commit('SET_PLAYER_STATE', 2);
    }
  },
  
  getters: {
    formattedTimeRange: state => {
      const formatTime = time => {
        const hours = Math.floor(time / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((time % 3600) / 60).toString().padStart(2, '0');
        const seconds = Math.floor(time % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      };
      
      return `${formatTime(state.startTime)} - ${formatTime(state.currentTime)}`;
    },
    
    isPlaying: state => state.playerState === 1
  }
}
